import { ChevronDown, SearchX, ArrowDownUp, ArrowUp, ArrowDown, Inbox, ClipboardX } from 'lucide-react';
import { useSuspenseQuery } from '@apollo/client/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import { Fragment, useMemo, useRef, useState, useTransition } from 'react';
import dayjs from 'dayjs';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { DepositStatusEnum, AssetContentTypeEnum, SyncStatusEnum, AssetTypeEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { formatBytes } from 'src/utils/formatBytes';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import { useDepositsSuggestChanges } from 'src/context/suggest-changes';
import { AssetTypeIcon, AssetAlert, depositStatusMap, syncStatusMap } from 'src/containers/Deposits';
import { EmptyBlock } from '@/components/ui/empty-block';
import Paginator from '@/components/ui/paginator';

import type { IAssetsQueryVariables } from 'src/graphql/actions/asset/assets.query';

import AssetsFilters from './AssetsFilters';
import AssetActions from './AssetActions';

type Props = {
  title: string;
  description: string;
  contentType: AssetContentTypeEnum | 'unlabeled';
  totalAssets: number;
};

type IAssetRow = {
  id: string;
  asset: {
    depositSource: string;
    provider?: string;
    type: AssetTypeEnum;
  };
  size: string;
  date: {
    latestDeposit: string;
    lastUpdateCheck: string;
  };
  syncStatus?: SyncStatusEnum;
  depositStatus: DepositStatusEnum;
  alert: {
    syncStatus?: SyncStatusEnum;
    depositStatus: DepositStatusEnum;
    error?: string;
  };
  action: {
    id: string;
    type: AssetContentTypeEnum | 'unlabeled';
    canRemove: boolean;
    isLast: boolean;
  };
};

const columnHelper = createColumnHelper<IAssetRow>();
const columns = [
  columnHelper.accessor('asset', {
    header: (context) => `Assets (${context.table.getRowModel().rows.length})`,
    cell: (props) => {
      const { depositSource, provider, type } = props.getValue();

      const providerName = provider ? PROVIDER_KEY_TO_NAME[provider as keyof typeof PROVIDER_KEY_TO_NAME] : '';

      return (
        <div className="flex items-center gap-2">
          <AssetTypeIcon type={type} provider={provider} />
          <div className="grow min-w-0">
            <div className="truncate" title={depositSource}>
              {depositSource}
            </div>
            {providerName && <div className="text-xs text-muted-foreground">{providerName}</div>}
          </div>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.asset.depositSource;
      const valueB = rowB.original.asset.depositSource;

      return valueA.localeCompare(valueB);
    },
    meta: {
      headerClassName: 'max-w-[352px]',
      cellClassName: 'max-w-[352px]',
    },
  }),
  columnHelper.accessor('size', {
    header: 'Size',
    cell: (props) =>
      props.getValue() ? <div className="whitespace-nowrap">{formatBytes(Number(props.getValue()))}</div> : null,
    enableSorting: false,
  }),
  columnHelper.accessor('date', {
    header: 'Last deposit',
    cell: (props) => {
      const { latestDeposit, lastUpdateCheck } = props.getValue();

      if (!latestDeposit) return null;

      const latestDepositDate = dayjs(latestDeposit).format('MMM DD, YYYY');
      const isLastCheckOverWeekAgo = lastUpdateCheck
        ? dayjs(lastUpdateCheck).isBefore(dayjs().subtract(1, 'week'))
        : false;
      const lastUpdateCheckDate = isLastCheckOverWeekAgo
        ? dayjs(latestDeposit).format('MMM DD, YYYY')
        : dayjs(lastUpdateCheck).fromNow();

      return (
        <Fragment>
          <div className="text-sm font-medium">{latestDepositDate}</div>
          {lastUpdateCheckDate && (
            <div className="text-[10px] text-muted-foreground whitespace-nowrap">Checked {lastUpdateCheckDate}</div>
          )}
        </Fragment>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.date.latestDeposit ? dayjs(rowA.original.date.latestDeposit).unix() : 0;
      const valueB = rowB.original.date.latestDeposit ? dayjs(rowB.original.date.latestDeposit).unix() : 0;

      return valueA - valueB;
    },
  }),
  columnHelper.accessor('syncStatus', {
    header: 'Sync status',
    cell: (props) => {
      const status = props.getValue();

      if (!status) return 'N/A';

      const syncStatus = syncStatusMap[status];

      if (!syncStatus) return 'N/A';

      const { label, textColor, dotColor } = syncStatus;

      return (
        <Badge variant="ghost" className={cn('gap-1 px-0', textColor)}>
          <span className={cn('rounded-full size-1.5', dotColor)} />
          {label}
        </Badge>
      );
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('depositStatus', {
    header: 'Deposit status',
    cell: (props) => {
      const { label, variant } = depositStatusMap[props.getValue()];

      return <Badge variant={variant}>{label}</Badge>;
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('alert', {
    header: '',
    cell: (props) => <AssetAlert {...props.getValue()} />,
    enableSorting: false,
    meta: {
      headerClassName: 'w-7',
      cellClassName: 'w-7',
    },
  }),
  columnHelper.accessor('action', {
    header: '',
    cell: (props) => <AssetActions depositSource={props.row.original.asset.depositSource} {...props.getValue()} />,
    enableSorting: false,
    meta: {
      headerClassName: 'w-16',
      cellClassName: 'w-16',
    },
  }),
];

const SuggestChangesAssetsBlock = ({ title, description, contentType, totalAssets }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isPending, startTransition] = useTransition();
  const {
    softwareEscrow: { id: softwareEscrowId, requiredContentTypes },
  } = useSoftwareEscrow();
  const initialFilters = {
    softwareEscrowId,
    perPage: 10,
    page: 1,
    labeled: contentType !== 'unlabeled',
    ...(contentType !== 'unlabeled' && { contentType }),
  };

  const [filters, setFilters] = useState<IAssetsQueryVariables>(initialFilters);
  const {
    data: { assets },
  } = useSuspenseQuery(AssetsDocument, {
    variables: filters,
  });
  const { state, allDeletedAssets } = useDepositsSuggestChanges();
  const totalContentAssetsRef = useRef(assets.nodesCount);
  const tableData = useMemo(() => {
    return assets.nodes.map((node) => ({
      id: node.id,
      asset: {
        depositSource: node.depositSource || '',
        provider: node.integration?.provider || '',
        type: node.type,
      },
      size: node.size || '',
      date: {
        latestDeposit: node.latestDeposit || '',
        lastUpdateCheck: node.lastUpdateCheck || '',
      },
      depositStatus: node.depositStatus || '',
      syncStatus: node.syncStatus || undefined,
      alert: {
        syncStatus: node.syncStatus || undefined,
        depositStatus: node.depositStatus || '',
        error: node.error || 'Unknown error',
      },
      action: {
        id: node.id,
        canRemove: !allDeletedAssets.some((asset) => asset.id === node.id),
        isLast: totalAssets - 1 === allDeletedAssets.length,
        type: contentType,
      },
    }));
  }, [assets.nodes, totalAssets, allDeletedAssets, contentType]);
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (!totalContentAssetsRef.current && contentType === 'unlabeled') return null;

  if (contentType !== 'unlabeled' && !(requiredContentTypes as readonly string[]).includes(contentType))
    return (
      <Card className="bg-[#F1F5F9]">
        <CardHeader>
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <EmptyBlock
            title={
              <Fragment>
                This asset type has been marked as <strong>Not required</strong> by the depositor.
              </Fragment>
            }
            icon={ClipboardX}
            withCard={false}
            classes={{
              title: 'text-sm',
              content: 'bg-[#F1F5F9]',
              icon: 'bg-[#F1F5F9]',
            }}
          />
        </CardContent>
      </Card>
    );

  if (!totalContentAssetsRef.current && contentType)
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <EmptyBlock
            title="No assets added"
            icon={Inbox}
            withCard={false}
            classes={{
              title: 'text-sm',
            }}
          />
        </CardContent>
      </Card>
    );

  const setDepositStatus = (depositStatus?: DepositStatusEnum) =>
    startTransition(() => setFilters((prevFilters) => ({ ...prevFilters, depositStatus })));

  const setSyncStatus = (syncStatus?: SyncStatusEnum) =>
    startTransition(() => setFilters((prevFilters) => ({ ...prevFilters, syncStatus })));

  const setQueryFilter = (queryFilter?: string) =>
    setFilters((prevFilters) => ({ ...prevFilters, queryFilter, page: 1 }));

  const onPageChange = (page: number) => startTransition(() => setFilters((prevFilters) => ({ ...prevFilters, page })));

  const infoItems = [
    {
      label: 'Total number of assets',
      value: totalContentAssetsRef.current,
    },
    {
      label: 'Removal requests',
      value: state[contentType as keyof typeof state].length,
      className: cn(state[contentType as keyof typeof state].length ? 'bg-accent' : 'bg-transparent'),
    },
  ];

  const setInitialFilters = () => setFilters(initialFilters);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Card>
        <CardHeader className="flex-row justify-between items-center gap-2 space-y-0 [&:has(+[data-state=open])]:pb-0">
          <div className="text-base font-semibold">{title}</div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              {isOpen ? 'Hide' : 'Show'} assets
              <ChevronDown className={cn('transition-transform rotate-0', isOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent className="pb-0">
          <CardContent className="space-y-6">
            <CardDescription>{description}</CardDescription>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {infoItems.map(({ label, value, className }) => (
                <div
                  key={label}
                  className={cn('bg-background-secondary border border-border rounded-lg p-5 space-y-1', className)}
                >
                  <div className="text-lg font-bold text-foreground">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <AssetsFilters
                depositStatus={filters.depositStatus ?? undefined}
                syncStatus={filters.syncStatus ?? undefined}
                queryFilter={filters.queryFilter ?? undefined}
                setDepositStatus={setDepositStatus}
                setSyncStatus={setSyncStatus}
                setQueryFilter={setQueryFilter}
                setInitialFilters={setInitialFilters}
              />
              <div className="border border-border rounded-md">
                <Table isLoading={isPending}>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(
                              'bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                              header.column.columnDef.meta?.headerClassName,
                            )}
                          >
                            {header.column.getCanSort() ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={header.column.getToggleSortingHandler()}
                                className="-ml-3 text-muted-foreground! text-xs! font-bold"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: <ArrowUp />,
                                  desc: <ArrowDown />,
                                }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                              </Button>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                cell.column.columnDef.meta?.cellClassName,
                                !row.original.action.canRemove && cell.column.id !== 'action' && 'opacity-30',
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                          <EmptyBlock
                            title="No results"
                            icon={SearchX}
                            withCard={false}
                            classes={{
                              title: 'text-sm',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <Paginator
                currentPage={filters.page || 1}
                totalPages={assets.pagesCount}
                onPageChange={onPageChange}
                showPreviousNext
                hideOnSinglePage
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default withSuspense(SuggestChangesAssetsBlock);
